import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { blogPosts, blogCategories } from '@/mocks/blog';

export default function BlogPage() {
  const [activeCategory, setActiveCategory] = useState('ყველა');
  const [searchQuery, setSearchQuery] = useState('');

  const filtered = useMemo(() => {
    let result = [...blogPosts];
    if (activeCategory !== 'ყველა') {
      result = result.filter((p) => p.category === activeCategory);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (p) =>
          p.title.toLowerCase().includes(q) ||
          p.excerpt.toLowerCase().includes(q) ||
          p.tags.some((t) => t.toLowerCase().includes(q))
      );
    }
    return result;
  }, [activeCategory, searchQuery]);

  const featured = blogPosts.filter((p) => p.featured);
  const showFeatured = activeCategory === 'ყველა' && !searchQuery.trim();

  return (
    <div className="min-h-screen bg-white font-sans">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-[#1C1C1C] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Premium%20construction%20materials%20showroom%20interior%20design%20inspiration%20elegant%20marble%20stone%20flooring%20display%20warm%20lighting%20architectural%20photography&width=1920&height=600&seq=blog-hero-phoenix&orientation=landscape"
            alt="ბლოგი"
            className="w-full h-full object-cover object-top opacity-15"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1C1C1C] to-[#1C1C1C]/80"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-8 md:px-14">
          <span className="inline-block text-[10px] font-bold tracking-[0.35em] uppercase text-[#ec3145] mb-4">ბლოგი</span>
          <h1 className="text-4xl md:text-6xl font-bold text-white tracking-tight mb-5 leading-tight">
            სტატიები და<br />
            <span className="text-[#ec3145]">გზამკვლევები</span>
          </h1>
          <p className="text-white/50 text-base md:text-lg max-w-xl leading-relaxed mb-8">
            ექსპერტული რჩევები, ინსტალაციის გზამკვლევები და ინდუსტრიის ტრენდები Phoenix-ის სპეციალისტებისგან.
          </p>
          {/* Search */}
          <div className="relative max-w-md">
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-white/40 text-sm"></i>
            <input
              type="text"
              placeholder="სტატიის ძიება..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-white/10 backdrop-blur-sm border border-white/20 text-white placeholder-white/35 pl-11 pr-4 py-3.5 text-sm focus:outline-none focus:border-[#ec3145] transition-colors duration-300"
            />
          </div>
        </div>
      </section>

      {/* Category filter */}
      <div className="sticky top-16 z-30 bg-white border-b border-[#1C1C1C]/8">
        <div className="max-w-7xl mx-auto px-8 md:px-14">
          <div className="flex items-center gap-1 overflow-x-auto py-3 scrollbar-hide">
            {blogCategories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-2 text-[11px] font-bold tracking-[0.12em] uppercase whitespace-nowrap transition-all duration-200 cursor-pointer ${
                  activeCategory === cat
                    ? 'bg-[#ec3145] text-white'
                    : 'text-[#1C1C1C]/50 hover:text-[#1C1C1C] hover:bg-[#F5F2EE]'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-8 md:px-14 py-16 md:py-20">

        {/* Featured posts */}
        {showFeatured && (
          <section className="mb-16">
            <h2 className="text-[10px] font-bold tracking-[0.3em] uppercase text-[#1C1C1C]/30 mb-6">გამორჩეული სტატიები</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {featured.map((post, i) => (
                <Link
                  key={post.id}
                  to={`/blog/${post.slug}`}
                  className={`group relative overflow-hidden cursor-pointer ${i === 0 ? 'lg:row-span-1' : ''}`}
                >
                  <div className="relative w-full overflow-hidden" style={{ height: '320px' }}>
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1C1C1C]/85 via-[#1C1C1C]/30 to-transparent"></div>
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <span className="inline-block text-[9px] font-bold tracking-[0.25em] uppercase text-[#ec3145] bg-white/10 backdrop-blur-sm px-2.5 py-1 mb-3">
                        {post.category}
                      </span>
                      <h3 className="text-lg md:text-xl font-bold text-white leading-snug mb-2 group-hover:text-[#ec3145] transition-colors duration-300">
                        {post.title}
                      </h3>
                      <p className="text-xs text-white/55 line-clamp-2 mb-3">{post.excerpt}</p>
                      <div className="flex items-center gap-3 text-[10px] text-white/40">
                        <span>{post.author}</span>
                        <span>·</span>
                        <span>{post.date}</span>
                        <span>·</span>
                        <span>{post.readTime} წთ კითხვა</span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* All posts grid */}
        <section>
          {!showFeatured && (
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-[10px] font-bold tracking-[0.3em] uppercase text-[#1C1C1C]/30">
                {filtered.length} სტატია
              </h2>
            </div>
          )}
          {showFeatured && (
            <h2 className="text-[10px] font-bold tracking-[0.3em] uppercase text-[#1C1C1C]/30 mb-6">ყველა სტატია</h2>
          )}

          {filtered.length === 0 ? (
            <div className="text-center py-20">
              <i className="ri-article-line text-4xl text-[#1C1C1C]/15 mb-4 block"></i>
              <p className="text-sm text-[#1C1C1C]/40">სტატიები ვერ მოიძებნა</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((post) => (
                <Link
                  key={post.id}
                  to={`/blog/${post.slug}`}
                  className="group flex flex-col bg-white border border-[#1C1C1C]/6 overflow-hidden hover:border-[#ec3145]/30 transition-all duration-300 cursor-pointer"
                >
                  <div className="relative w-full overflow-hidden" style={{ height: '200px' }}>
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute top-3 left-3">
                      <span className="text-[9px] font-bold tracking-[0.2em] uppercase text-white bg-[#ec3145] px-2.5 py-1">
                        {post.category}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-col flex-1 p-5">
                    <h3 className="text-sm font-bold text-[#1C1C1C] leading-snug mb-2 group-hover:text-[#ec3145] transition-colors duration-300 line-clamp-2">
                      {post.title}
                    </h3>
                    <p className="text-xs text-[#1C1C1C]/45 leading-relaxed mb-4 line-clamp-3 flex-1">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center justify-between pt-3 border-t border-[#1C1C1C]/6">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-[#ec3145]/10 flex items-center justify-center">
                          <i className="ri-user-line text-[#ec3145] text-[10px]"></i>
                        </div>
                        <span className="text-[10px] text-[#1C1C1C]/40">{post.author}</span>
                      </div>
                      <div className="flex items-center gap-1 text-[10px] text-[#1C1C1C]/30">
                        <i className="ri-time-line text-[10px]"></i>
                        {post.readTime} წთ
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>
      </div>

      <Footer />
    </div>
  );
}